/*
 * PDP colour picker.
 *
 * Every swatch is a link to a sibling product. Instead of a full navigation
 * we fetch the sibling's main-product section and let Dawn's <product-info>
 * swap itself in place (the same code path Dawn uses for combined listings).
 * Sibling sections are prefetched on idle so the swap is usually instant.
 *
 * Anything that goes wrong falls back to a normal navigation.
 */
(function () {
  if (window.macadeAltPicker) return;
  window.macadeAltPicker = true;

  const LINK = '.alternative-product';
  const CACHE_TTL = 5 * 60 * 1000;
  const MAX_PREFETCH = 12;
  const cache = new Map(); // url -> { at, promise }
  let inFlight = null;

  const designMode = () => !!(window.Shopify && window.Shopify.designMode);
  const productInfo = () =>
    document.querySelector("product-info[id^='MainProduct']");
  const pathOf = (url) => new URL(url, window.location.origin).pathname;

  function sectionUrl(url, info) {
    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}section_id=${info.dataset.section}`;
  }

  function fetchSection(url) {
    const hit = cache.get(url);
    if (hit && Date.now() - hit.at < CACHE_TTL) return hit.promise;

    const info = productInfo();
    if (!info) return Promise.reject(new Error('no product-info'));

    const promise = fetch(sectionUrl(url, info)).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    });
    promise.catch(() => cache.delete(url));
    cache.set(url, { at: Date.now(), promise });
    return promise;
  }

  function siblingUrls(root) {
    return Array.from(root.querySelectorAll(LINK))
      .filter((link) => !link.hasAttribute('aria-current'))
      .map((link) => link.getAttribute('href'))
      .filter(Boolean);
  }

  function prefetchSiblings() {
    const info = productInfo();
    if (!info) return;
    // One at a time so we don't compete with the page's own requests.
    siblingUrls(info)
      .slice(0, MAX_PREFETCH)
      .reduce(
        (chain, url) => chain.then(() => fetchSection(url).catch(() => {})),
        Promise.resolve()
      );
  }

  function schedulePrefetch() {
    if (designMode()) return;
    if (navigator.connection && navigator.connection.saveData) return;
    const idle =
      window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1500));
    const go = () => idle(prefetchSiblings, { timeout: 4000 });
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  }

  function recordHistory(url) {
    // Mirrors macade-history-recorder.js, which only runs on real page loads.
    try {
      const path = JSON.parse(localStorage.getItem('breadcrumbPath') || '[]');
      path.push(pathOf(url));
      localStorage.setItem('breadcrumbPath', JSON.stringify(path.slice(-500)));
    } catch (_) {
      /* storage unavailable */
    }
  }

  function updateDocumentMeta(oldInfo, newInfo, url) {
    const oldTitle = oldInfo.querySelector('.product__title h1');
    const newTitle = newInfo.querySelector('.product__title h1');
    if (oldTitle && newTitle) {
      const from = oldTitle.textContent.trim();
      const to = newTitle.textContent.trim();
      if (from && to && document.title.includes(from)) {
        document.title = document.title.replace(from, to);
      }
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = new URL(url, window.location.origin).href;
  }

  // Keep the swatch row where the shopper sees it. Dawn inserts the new
  // section and hides the old one, which defeats the browser's own scroll
  // anchoring, and async content (Yotpo stars, images) keeps shifting things
  // for a moment afterwards.
  const swatchGrid = (root) => root.querySelector(LINK)?.closest('.grid');

  function measureAnchor(info, trigger) {
    const grid = (trigger && trigger.closest('.grid')) || swatchGrid(info);
    return grid ? grid.getBoundingClientRect().top : null;
  }

  function keepAnchorInPlace(info, top) {
    const grid = swatchGrid(info);
    if (!grid) return;

    const html = document.documentElement;
    const correct = () => {
      const delta = grid.getBoundingClientRect().top - top;
      if (Math.abs(delta) < 0.5) return;
      const previous = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollBy(0, delta);
      html.style.scrollBehavior = previous;
    };
    correct();

    // Re-pin while the new section settles, but let go as soon as the
    // shopper scrolls themselves. The first frames after the swap shift
    // things without changing the section's height, so check every frame
    // for a short while; the ResizeObserver covers later async content.
    const FRAME_WINDOW = 700;
    const TOTAL_WINDOW = 2000;
    const started = performance.now();
    let stopped = false;
    let frame = 0;
    const observer = new ResizeObserver(correct);
    observer.observe(info);
    const tick = () => {
      if (stopped) return;
      correct();
      if (performance.now() - started < FRAME_WINDOW) {
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    const stop = () => {
      stopped = true;
      observer.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      ['wheel', 'touchstart', 'keydown'].forEach((type) =>
        window.removeEventListener(type, stop)
      );
    };
    const timer = setTimeout(stop, TOTAL_WINDOW);
    ['wheel', 'touchstart', 'keydown'].forEach((type) =>
      window.addEventListener(type, stop, { passive: true })
    );
  }

  // App bundles Shopify loads once in <head> that mount into markup inside
  // <product-info> and therefore have to run again after a swap. Matched on
  // filename so the extension version in the URL doesn't matter.
  //
  // - Voyado "notify me when back in stock": a React app that mounts into
  //   #voyado_app_block_bis and listens to the product form's variant input.
  //   Dawn has already renamed the ids in the old block, so a re-run finds
  //   the new block and the new form.
  const REMOUNT_SCRIPTS = [
    { file: 'app-block-bis-index', needs: '#voyado_app_block_bis' },
  ];

  function remountAppScripts(info) {
    REMOUNT_SCRIPTS.forEach(({ file, needs }) => {
      if (!info.querySelector(needs)) return;
      const original = document.querySelector(`head script[src*="${file}"]`);
      if (!original) return;
      const script = document.createElement('script');
      script.src = original.src;
      script.async = true;
      script.dataset.macadeRemount = file;
      script.onload = script.onerror = () => script.remove();
      document.head.appendChild(script);
    });
  }

  function afterSwap(url, anchorTop) {
    const info = productInfo();
    if (!info) return;

    remountAppScripts(info);

    if (
      info.hasAttribute('data-zoom-on-hover') &&
      typeof enableZoomOnHover === 'function'
    ) {
      enableZoomOnHover(2);
    }
    if (
      window.yotpoWidgetsContainer &&
      typeof window.yotpoWidgetsContainer.initWidgets === 'function'
    ) {
      window.yotpoWidgetsContainer.initWidgets();
    }

    const current = info.querySelector(`${LINK}[aria-current]`);
    if (current) current.focus({ preventScroll: true });

    if (anchorTop !== null) keepAnchorInPlace(info, anchorTop);

    document.dispatchEvent(
      new CustomEvent('macade:product-swapped', {
        bubbles: true,
        detail: { url },
      })
    );
    schedulePrefetch();
  }

  function swapTo(url, { push = true, trigger = null } = {}) {
    const info = productInfo();
    if (!info || typeof info.handleSwapProduct !== 'function') {
      window.location.href = url;
      return;
    }
    if (pathOf(info.dataset.url) === pathOf(url)) return;

    inFlight = url;
    info.setAttribute('aria-busy', 'true');
    if (trigger) trigger.classList.add('alternative-product--loading');

    fetchSection(url)
      .then((text) => {
        if (inFlight !== url) return; // a later click superseded this one

        const html = new DOMParser().parseFromString(text, 'text/html');
        const next = html.querySelector('product-info');
        if (!next) throw new Error('response has no <product-info>');

        // Every script the section needs is already loaded on this page.
        // Dawn re-injects <script> tags when swapping, so drop the external
        // ones; inline data scripts (variant JSON, app data) stay.
        next.querySelectorAll('script[src]').forEach((s) => s.remove());

        const anchorTop = measureAnchor(info, trigger);

        if (push) history.pushState({ macadeProductUrl: url }, '', url);
        updateDocumentMeta(info, next, url);
        info.handleSwapProduct(url, false)(html);
        // Dawn's updateURL replaceState()s the entry with ?variant= and an
        // empty state; put our marker back on it.
        history.replaceState({ macadeProductUrl: url }, '', location.href);

        recordHistory(url);
        afterSwap(url, anchorTop);
      })
      .catch((error) => {
        console.error(
          '[macade] product swap failed, navigating instead',
          error
        );
        window.location.href = url;
      })
      .finally(() => {
        if (inFlight === url) inFlight = null;
        info.removeAttribute('aria-busy');
        if (trigger) trigger.classList.remove('alternative-product--loading');
      });
  }

  // Click: swap in place. Modifier clicks and middle clicks keep native behaviour.
  document.addEventListener('click', (event) => {
    const link = event.target.closest(LINK);
    if (!link || designMode()) return;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const url = link.getAttribute('href');
    if (!url || !link.closest('product-info')) return;

    event.preventDefault();
    if (link.hasAttribute('aria-current')) return;
    swapTo(url, { trigger: link });
  });

  // Hover / touch / focus: warm the cache for swatches not prefetched yet.
  const warm = (event) => {
    const link = event.target.closest(LINK);
    if (!link || link.hasAttribute('aria-current') || designMode()) return;
    const url = link.getAttribute('href');
    if (url) fetchSection(url).catch(() => {});
  };
  document.addEventListener('mouseover', warm, { passive: true });
  document.addEventListener('touchstart', warm, { passive: true });
  document.addEventListener('focusin', warm);

  // Show the hovered swatch's colour name next to "Color:".
  document.addEventListener('mouseover', (event) => {
    const img = event.target.closest(`${LINK} img`);
    if (!img) return;
    const scope = img.closest('product-info') || document;
    const label = scope.querySelector('.color-alt');
    if (label) label.textContent = img.dataset.color || '';
  });

  // Back / forward between swapped products.
  window.addEventListener('popstate', () => {
    const info = productInfo();
    if (!info) return;
    if (pathOf(info.dataset.url) !== window.location.pathname) {
      swapTo(window.location.pathname, { push: false });
    }
  });

  schedulePrefetch();
})();
